import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import { AppDialogHelper } from 'general/components/AppDialog';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSButton, {
    KTBSButtonColor,
    KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTBSDropdown, { KTBSDropdownDirections } from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import EmptyView from 'general/components/Others/EmptyView';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AppRoute from 'general/constants/AppRoute';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import BreadcrumbHelper from 'general/components/CustomBreadcrumb/BreadcrumbHelper';
import AppConstants from 'general/constants/AppConstants';
import Utils from 'general/utils/Utils';
import KTBSProgressBar from 'general/components/OtherKeenComponents/KTBSProgressBar';
import UIHelper from 'general/helpers/UIHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import MenuActionDropdown from 'general/components/Others/MenuActionDropdown';
import DropdownFilterTable from 'general/components/DropdownFilterTable';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import CustomDateRangePicker, { dateFilter, getDateFilterOption } from 'general/components/Others/CustomDateRangePicker';

SubAgencyView.propTypes = {};

const defaultColumnIds = ['AGENCY_CODE', 'AGENCY_NAME', 'AGENCY_TYPE', 'PHONE', 'EMAIL', 'TAXCODE/CCCD', 'REVENUE', 'KPI', 'STATUS', 'ADDRESS', 'CREATEDBY', 'DATE'];

function SubAgencyView(props) {
    // ---- params ----
    const router = useRouter();
    const { t } = useTranslation();
    const { agencyId, subAgenciesList, subAgencyTotal, onFilterChanged, filter, handleGetSubAgenciesList } = props;
    const app = useSelector((state) => state.app);
    const { user } = useSelector((state) => state.auth);
    const [operatorList, setOperatorList] = useState([]);
    const [filterDropdown, setFilterDropdown] = useState(filter);
    const fullColumns = useMemo(() => {
        return [
            {
                id: 'AGENCY_CODE',
                name: UIHelper.buildTableColumn('Mã đại lý'),
                selector: (row) => row?.agencyCode ?? row?.agencyId,
                width: '120px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'AGENCY_NAME',
                name: UIHelper.buildTableColumn('Tên đại lý'),
                selector: (row) => row.name ?? 'N/A',
                minWidth: '240px',
                style: tableStyle.columns.link,
            },
            {
                id: 'AGENCY_TYPE',
                name: UIHelper.buildTableColumn('Loại đại lý'),
                selector: (row) => row.type ? t(`accountType_${row.type}`) : 'N/A',
                style: tableStyle.columns.normal,
                width: '120px',
            },
            {
                id: 'PHONE',
                name: UIHelper.buildTableColumn('Số điện thoại'),
                selector: (row) => row.phone ?? '',
                style: tableStyle.columns.normal,
                width: '140px',
            },
            {
                id: 'EMAIL',
                name: UIHelper.buildTableColumn('Email'),
                selector: (row) => {
                    return (
                        <KTTooltip offset={[0, 0]} text={row?.email ?? ''}>
                            <span>{row?.email ?? ''}</span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.normal,
                minWidth: '250px',
            },
            {
                id: 'TAXCODE/CCCD',
                name: UIHelper.buildTableColumn('MST/CCCD'),
                selector: (row) =>
                    row.type === AppConstants.accountType.individual
                        ? row.idCardNumber?.length > 0
                            ? row.idCardNumber
                            : row.taxCode?.length > 0
                                ? row.taxCode
                                : ''
                        : row.taxCode?.length > 0
                            ? row.taxCode
                            : '',
                style: tableStyle.columns.normal,
                width: '150px',
            },
            {
                id: 'REVENUE',
                name: UIHelper.buildTableColumn('Doanh thu trong tháng', 'VNĐ'),
                selector: (row) => Utils.formatNumber(row?.revenue),
                width: '160px',
                right: true,
                style: tableStyle.columns.normal,
            },
            {
                id: 'KPI',
                name: UIHelper.buildTableColumn('Tiến độ KPI'),
                selector: (row) => (
                    <div className='w-100'>
                        <KTBSProgressBar
                            completed={row.saleKPI || row.packageKPI
                                ? Math.round((row.revenue / (row.saleKPI + row.packageKPI)) * 100)
                                : 0
                            }
                            value={row.revenue}
                            maxValue={row.saleKPI + row.packageKPI}
                            bgColor={AppColors.primary}
                            showValue={false}
                            width={120}
                        />
                    </div>
                ),
                style: {
                    ...tableStyle.columns.normal,
                    overflow: 'visible',
                },
                width: '150px',
            },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái'),
                width: '150px',
                selector: (row) => (
                    <KTLabel
                        text={t(`agencyStatus_${row.status}`)}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${DealerHelper.getAgencyStatusColor(row.status)}`}
                    />
                ),
            },
            {
                id: 'ADDRESS',
                name: UIHelper.buildTableColumn('Địa chỉ'),
                selector: (row) => {
                    const area = ChargeHelper.Utils.getAreaById([areaTree], row?.areaId);
                    let parentArea;
                    if (area?.typeCode === 'W') {
                        parentArea = ChargeHelper.Utils.getAreaById([areaTree], area?.parentId);
                    }
                    const addressResult = [row.address, area?.name, parentArea?.name]
                        .filter(Boolean)
                        .join(', ');
                    return (
                        <KTTooltip text={addressResult} offset={[0, 0]}>
                            <span>{addressResult?.length > 0 ? addressResult : ''}</span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.normal,
                minWidth: '250px',
            },
            {
                id: 'CREATEDBY',
                name: UIHelper.buildTableColumn('Người tạo'),
                selector: (row) => {
                    return (
                        <KTTooltip offset={[0, 0]} text={row?.createdBy?.fullname ?? row?.name}>
                            <span
                                className="cursor-pointer hover-opacity-60"
                                onClick={() => {
                                    DealerHelper.navigateToParentAgency(row?.createdBy ?? row);
                                }}
                            >
                                {row?.createdBy?.fullname ?? row?.name}
                            </span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.link,
                width: '200px',
            },
            {
                id: 'DATE',
                name: UIHelper.buildTableColumn('Ngày tạo'),
                selector: (row) => Utils.formatDateTimeToString(row.createdAt, 'DD/MM/YYYY - HH:mm'),
                style: tableStyle.columns.normal,
                width: '160px',
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                width: '130px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                wrap: true,
                cell: (row) => {
                    return (
                        <MenuActionDropdown
                            menuAction={[
                                {
                                    iconElement: <i className="fa-regular fa-eye" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                                    text: t('Xem chi tiết'),
                                    click: () => {
                                        handleClickedRow(row);
                                    },
                                    show: true,
                                },
                                {
                                    iconElement: <i className="fa-regular fa-bolt" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                                    text: t('Kích hoạt tài khoản'),
                                    click: () => {
                                        DealerHelper.handleAcceptAgency(row, () => {
                                            handleGetSubAgenciesList();
                                        });
                                    },
                                    show: row?.status === 'REGISTERED'
                                },
                                {
                                    iconElement: <i className="fa-regular fa-pen-to-square" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                                    text: t('edit'),
                                    click: () => {
                                        Utils.openInNewTab(`${AppRoute.saleList}/${row?.agencyId}/edit`);
                                    },
                                    show: !row?.numberOfSignedContracts
                                },
                                {
                                    iconElement: <i className="fa-regular fa-plus-circle" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                                    text: t('addOrder'),
                                    click: () => {
                                        router.navigate(`add-order?sale=${row?.agencyId}`);
                                    },
                                    show: row?.status !== 'REGISTERED'
                                },
                                {
                                    iconElement: <i className="far fa-square" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                                    text: t('stopWorking'),
                                    click: () => {
                                        DealerHelper.handleStopAgency(row, () => {
                                            handleGetSubAgenciesList();
                                        });
                                    },
                                    show: row?.status === 'NORMAL'
                                },
                                {
                                    iconElement: <i className="far fa-play" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                                    text: t('unStopWorking'),
                                    click: () => {
                                        DealerHelper.handleActiveAgency(row, () => {
                                            handleGetSubAgenciesList();
                                        });
                                    },
                                    show: row?.status === 'SUSPENDED'
                                },
                                {
                                    iconElement: <i className="far fa-bullseye-arrow" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                                    text: t('Thiết lập KPI tháng'),
                                    click: () => {
                                        DealerHelper.setMonthKPI(row, () => {
                                            handleGetSubAgenciesList();
                                        });
                                    },
                                    show: row?.status !== 'REGISTERED'
                                },
                                {
                                    iconElement: <i className={`fa-regular fa-trash-can`} style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}></i>,
                                    text: t('Xóa đại lý'),
                                    click: () => {
                                        DealerHelper.handleDeleteAgency(row, () => {
                                            handleGetSubAgenciesList();
                                        });
                                    },
                                    show: !row?.numberOfSignedContracts
                                },
                            ]}
                        />
                    );
                },
            },
        ];
    }, [t, operatorList]);

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = `${router.pathname}${router.location.hash}`;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(fullColumns?.map((row) => row?.id).filter(Boolean));
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        saveChanges,
        onSelectAll,
        onUnSelectAll,
        fullColumnsIdSorted
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
    // khu vuc
    const areaTree = useSelector((state) => state.app.areaTree);
    const areaProvinceList = useMemo(() => {
        return ChargeHelper.Utils.getAreaListByType([areaTree], 'P');
    }, [areaTree]);
    const areaProvinceOptions = useMemo(() => {
        return _.map(areaProvinceList, (item) => {
            return {
                name: item.name,
                value: item.areaId,
            };
        });
    }, [areaProvinceList]);
    const [selectedAreaProvince, setSelectedAreaProvince] = useState();
    const areaDistrictList = useMemo(() => {
        if (!selectedAreaProvince) return [];
        return ChargeHelper.Utils.getSubAreaListByParentId([areaTree], selectedAreaProvince?.areaId);
    }, [areaTree, selectedAreaProvince]);
    const areaDistrictOptions = useMemo(() => {
        return _.map(areaDistrictList, (item) => {
            return {
                name: item.name,
                value: item.areaId,
            };
        });
    }, [areaDistrictList]);
    const [selectedAreaDistrict, setSelectedAreaDistrict] = useState();
    useEffect(() => {
        const areaId = filterDropdown.areaId;
        if (Utils.isValid(areaId)) {
            const area = ChargeHelper.Utils.getAreaById([areaTree], areaId);
            if (area?.typeCode === 'P') {
                setSelectedAreaProvince(area);
            } else {
                setSelectedAreaDistrict(area);
                const parentAreaId = area?.parentId;
                if (Utils.isValid(parentAreaId)) {
                    setSelectedAreaProvince(ChargeHelper.Utils.getAreaById([areaTree], parentAreaId));
                }
            }
        }
    }, [filterDropdown?.areaId, areaTree]);

    // ---- methods ----

    const classifyOperators = (operators) => {
        setOperatorList(
            operators?.map((operator) => ({ operatorId: operator.operatorId, name: operator.name }))
        );
    };

    useEffect(() => {
        if (app.appConfig) {
            classifyOperators(app.appConfig.operators ?? []);
        }
    }, [app.appConfig]);

    const handleShowAddSubAgencyModel = () => {
        BreadcrumbHelper.setBreadcrumPath(t('addDealer'), `${router?.pathname}/add`);
        router.navigate('add');
    }

    const handleClickedRow = (row) => {
        Utils.openInNewTab(`${AppRoute.saleList}/${row.agencyId}`);
    };

    const handleFilterChanged = (filter) => {
        if (onFilterChanged) {
            onFilterChanged(filter);
        }
    };

    const updateFilter = (key, value) => {
        setFilterDropdown((prevFilter) => {
            const newFilter = { ...prevFilter, [key]: value };
            handleFilterChanged(newFilter);
            return newFilter;
        });
    };

    const FilterComponentList = ({ isInDropdown = false }) => (
        <div className={`${isInDropdown ? 'row' : 'w-100'} d-flex  flex-row  flex-wrap`}>
            {/* loai dai ly */}
            <KTDropdownFilter
                label={t('agencyType')}
                filterKey="type"
                options={DealerHelper.filterAgencyTypeOptions()}
                selectedValue={!isInDropdown ? filter.type : filterDropdown.type}
                isInDropdown={isInDropdown}
                additionalClassName={`${isInDropdown ? 'pr-2 pt-4' : 'pl-0 pr-4'}`}
                handleChange={(value) => {
                    if (isInDropdown) {
                        setFilterDropdown({ ...filterDropdown, type: value });
                    } else {
                        updateFilter('type', value);
                        updateFilter('page', 0);
                    }
                }}
                direction={KTBSDropdownDirections.up}
            />

            {/* trang thai */}
            <KTDropdownFilter
                label={t('status')}
                filterKey="status"
                options={DealerHelper.filterStatusOptions()}
                selectedValue={
                    !isInDropdown
                        ? filter.status ||
                        (filter.hasSignedContract ? AppConstants.agencyStatus.signed : '')
                        : filterDropdown.status ||
                        (filter.hasSignedContract || filterDropdown.hasSignedContract
                            ? AppConstants.agencyStatus.signed
                            : '')
                }
                isInDropdown={isInDropdown}
                additionalClassName={`${isInDropdown ? 'pl-2 pt-4' : 'pl-0 pr-4'}`}
                handleChange={(value) => {
                    const isSigned = value === 'SIGNED';
                    const newStatus = isSigned ? '' : value;
                    const newHasSignedContract = isSigned ? 1 : '';

                    if (isInDropdown) {
                        setFilterDropdown({
                            ...filterDropdown,
                            status: newStatus,
                            hasSignedContract: newHasSignedContract,
                        });
                    } else {
                        updateFilter('status', newStatus);
                        updateFilter('hasSignedContract', newHasSignedContract);
                        updateFilter('page', 0);
                    }
                }}
                direction={KTBSDropdownDirections.up}
            />

            <KTDropdownFilter
                label={t('Người tạo')}
                filterKey="createdBy"
                options={DealerHelper.filterCreatedByOptions()}
                selectedValue={filterDropdown.createdBy}
                isInDropdown={isInDropdown}
                additionalClassName={`${isInDropdown ? 'pr-2 pt-2' : 'pl-0 pr-0 pr-xl-4'}`}
                handleChange={(value) => {
                    if (isInDropdown) {
                        setFilterDropdown({ ...filterDropdown, createdBy: value });
                    } else {
                        updateFilter('createdBy', value);
                    }
                }}
                direction={KTBSDropdownDirections.up}
            />

            {isInDropdown && (
                <>

                    <div className="col-12 py-2">
                        <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                            {t('Khoảng thời gian thống kê')}
                        </label>
                        <CustomDateRangePicker
                            toggleElementBorderRadius={5}
                            alignRight={true}
                            initialValue={
                                filterDropdown.selectedValue?.length > 0
                                    ? filterDropdown.selectedValue
                                    : dateFilter.tatCaThoiGian
                            }
                            onPressSave={(selectedValue, startDate, endDate) => {
                                const updateFilterDropdown = {
                                    ...filterDropdown,
                                    from: startDate,
                                    to: endDate,
                                    selectedValue: selectedValue,
                                };
                                setFilterDropdown(updateFilterDropdown);
                            }}
                            filterComponent={filterDropdown}
                            direction={KTBSDropdownDirections.up}
                        />
                    </div>

                    <KTDropdownFilter
                        label={t('city')}
                        filterKey="areaId"
                        options={areaProvinceOptions}
                        selectedOption={selectedAreaProvince}
                        selectedValue={filterDropdown.areaId}
                        isInDropdown={isInDropdown}
                        additionalClassName="pr-2 pt-2"
                        searchable={true}
                        handleChange={(value) => {
                            const province = _.find(areaProvinceList, (item) => item.areaId === value);
                            setSelectedAreaProvince(province);
                            setSelectedAreaDistrict('');
                            if (isInDropdown) {
                                setFilterDropdown({ ...filterDropdown, areaId: value });
                            } else {
                                updateFilter('areaId', value);
                            }
                        }}
                        direction={KTBSDropdownDirections.up}
                    />

                    <KTDropdownFilter
                        label={t('district')}
                        filterKey="areaId"
                        options={areaDistrictOptions}
                        selectedOption={selectedAreaDistrict}
                        selectedValue={filterDropdown.areaId}
                        isInDropdown={isInDropdown}
                        additionalClassName="pl-2 pt-2"
                        searchable={true}
                        handleChange={(value) => {
                            setSelectedAreaDistrict(_.find(areaDistrictList, (item) => item.areaId === value));
                            if (isInDropdown) {
                                setFilterDropdown({ ...filterDropdown, areaId: value });
                            } else {
                                updateFilter('areaId', value);
                            }
                        }}
                        direction={KTBSDropdownDirections.up}
                    />
                </>
            )}
        </div>
    );

    return (
        <div>
            {/* header */}
            <div className="w-100 d-flex flex-row align-items-end flex-wrap">
                <div className={`col-lg-3 pl-0 py-2 pr-0 pr-xl-4 pr-lg-4`}>
                    <label htmlFor="search" className="mb-0 mr-2 font-weight-bolder">
                        {t('search')}
                    </label>
                    <KTFormSearch
                        name="search-bar"
                        placeholder="Theo mã đại lý, tên đại lý, số điện thoại, email, MST/CCCD..."
                        value={filter.q}
                        onSubmit={(text) => {
                            handleFilterChanged({
                                q: text,
                                page: 0
                            });
                            setFilterDropdown({ ...filter, q: text, page: 0 });
                        }}
                    />
                </div>
                <div className="col-xl-7 col-lg-9 px-0">
                    <FilterComponentList />
                </div>

                {/* bo loc */}
                <div className="py-2">
                    <DropdownFilterTable
                        filterComponent={[
                            <div className={`${'col-12 p-0'}`}>
                                <KTFormSearch
                                    name="search-bar"
                                    placeholder={t(
                                        'Theo mã đại lý, tên đại lý, số điện thoại, email, MST/CCCD...'
                                    )}
                                    value={filterDropdown.q}
                                    onSubmit={(text) => {
                                        const updateFilterDropdown = { ...filterDropdown, q: text };
                                        setFilterDropdown(updateFilterDropdown);
                                    }}
                                />
                            </div>,
                            <FilterComponentList
                                isInDropdown={true}
                                onChange={(filter) => {
                                    setFilterDropdown({
                                        ...filterDropdown,
                                        ...filter,
                                    });
                                }}
                            />,
                        ]}
                        resetDefaultTable={() => {
                            const updateFilterDropdown = {
                                ...filterDropdown,
                                q: '',
                                type: '',
                                status: '',
                                from: getDateFilterOption(dateFilter.tatCaThoiGian)[0].format('YYYY-MM-DD'),
                                to: getDateFilterOption(dateFilter.tatCaThoiGian)[1].format('YYYY-MM-DD'),
                                selectedValue: dateFilter.tatCaThoiGian,
                                areaId: '',
                                createdBy: '',
                                deepLevel: '',
                                hasSignedContract: '',
                            };
                            setFilterDropdown(updateFilterDropdown);
                            handleFilterChanged({
                                q: '',
                                type: '',
                                status: '',
                                from: getDateFilterOption(dateFilter.tatCaThoiGian)[0].format('YYYY-MM-DD'),
                                to: getDateFilterOption(dateFilter.tatCaThoiGian)[1].format('YYYY-MM-DD'),
                                selectedValue: dateFilter.tatCaThoiGian,
                                areaId: '',
                                createdBy: '',
                                deepLevel: '',
                                hasSignedContract: '',
                            })
                            setSelectedAreaProvince('');
                            setSelectedAreaDistrict('');
                        }}
                        saveFilter={() => {
                            handleFilterChanged({ ...filterDropdown })
                        }}
                        isDefaultFilter={Utils.checkDefaultFilter(
                            ['role', 'page', 'limit', 'from', 'to', 'selectedValue', 'parentAgencyId'],
                            filter
                        )}
                        direction={KTBSDropdownDirections.up}
                    />
                </div>

                {/* cau hinh cot */}
                {/* <div className="pl-0 py-2">
                    <DropdownTableConfigColumn
                        columnIds={columnIds}
                        fullColumns={fullColumns}
                        fullColumnIds={fullColumnsIdSorted}
                        onChangeCheckboxValue={onChangeCheckboxValue}
                        saveChanges={saveChanges}
                        resetDefaultTableColumn={() => {
                            setFullColumnIds(defaultColumnIds);
                            resetDefaultTableColumn();
                        }}
                        onChangeFullColumnIds={(ids) => {
                            setFullColumnIds(ids);
                            const key = prefKey + '_FULL_COLUMN_IDS';
                            localStorage.setItem(key, ids.join('|'));
                        }}
                        onSelectAll={onSelectAll}
                        onUnSelectAll={onUnSelectAll}
                        direction={KTBSDropdownDirections.up}
                    />
                </div> */}

                {/* Actions */}
                <div className="ml-4 py-2">
                    {/* Thêm đại lý liên kết */}
                    <KTBSButton
                        color={KTBSButtonColor.success}
                        title={t('addDealer')}
                        size={KTBSButtonSize.default}
                        borderRadius={5}
                        buttonTitleFontWeight="600"
                        iconElement={<i className="fa-regular fa-circle-plus" />}
                        onPress={handleShowAddSubAgencyModel}
                    />
                </div>
            </div>

            {/* Body */}
            <div className="mt-4">
                <KTTable
                    columns={columns}
                    items={subAgenciesList}
                    selectableRows={false}
                    striped={false}
                    compactGrid={false}
                    onClickedRow={() => { }}
                    onSelectedRowsChanged={(rows) => { }}
                    onDoubleClickedRow={(rows) => { }}
                    noDataComponent={
                        <EmptyView
                            icon={imgEmptyNoData}
                            title="Không có dữ liệu để hiển thị"
                            text={
                                <div className="text-center">
                                    <span>
                                        Hiện không có dữ liệu để hiển thị. Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.
                                    </span>
                                </div>
                            }
                        />
                    }
                />
            </div>

            {/* Phân trang */}
            {!_.isEmpty(subAgenciesList) && (
                <div className="mt-4">
                    <KTPagination
                        currentPage={filter.page + 1}
                        rowCount={subAgencyTotal}
                        rowsPerPage={filter.limit}
                        currentItemInPageCount={subAgenciesList?.length}
                        onChangePage={(newPage) => {
                            const iNewPage = parseInt(newPage);
                            handleFilterChanged({
                                page: iNewPage - 1,
                            });
                        }}
                        onChangeRowsPerPage={(newRowsPerPage) => {
                            const iNewRowsPerPage = parseInt(newRowsPerPage);
                            handleFilterChanged({
                                limit: iNewRowsPerPage,
                                page: 0,
                            });
                        }}
                        loading={false}
                        color={KTPaginationColor.success}
                        appendText="đại lý"
                    />
                </div>
            )}
        </div>
    );
}

export default SubAgencyView;
